import { Box, styled } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactFlow, {
  Background,
  ControlButton,
  Controls,
  type NodeChange,
  Panel,
  ReactFlowProvider,
  useReactFlow,
  useStore,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { AddStepNode } from 'src/pages/Settings/workflows/components/nodes/add-step-node';
import { WorkflowCanvasToolbar } from 'src/pages/Settings/workflows/components/workflow-canvas-toolbar';
import { WorkflowEdge } from 'src/pages/Settings/workflows/components/workflow-edge';
import { WorkflowFocusDrawer } from 'src/pages/Settings/workflows/components/workflow-focus-drawer';
import { WorkflowNode } from 'src/pages/Settings/workflows/components/workflow-node';
import { WorkflowNodeType } from 'src/pages/Settings/workflows/utils/workflow-definition';
import {
  childStepIds,
  parentStepId,
} from 'src/pages/Settings/workflows/utils/workflow-folding';
import {
  ADD_STEP_NODE_TYPE,
  STEP_EDGE_TYPE,
  STEP_NODE_TYPE,
  WorkflowGraphEdge,
  WorkflowGraphNode,
  WorkflowLayoutDirection,
} from 'src/pages/Settings/workflows/utils/workflow-helpers';
import { useWorkflowEditor } from 'src/pages/Settings/workflows/workflow-editor-context';

const nodeTypes = { [STEP_NODE_TYPE]: WorkflowNode, [ADD_STEP_NODE_TYPE]: AddStepNode };
const edgeTypes = { [STEP_EDGE_TYPE]: WorkflowEdge };
// above every other card, so the open one sits over its neighbours instead of pushing them
const OPEN_NODE_Z_INDEX = 1000;
/** Long enough for the card's Collapse to finish opening, so its full height is measured. */
const OPEN_CARD_SETTLE_MS = 400;
const OPEN_CARD_MARGIN_PX = 16;

/** How far to move along one axis to bring [start, start + size) inside [margin, viewport - margin]. */
const panIntoView = (start: number, size: number, viewport: number) => {
  const end = start + size;
  if (end > viewport - OPEN_CARD_MARGIN_PX) {
    // too tall or wide to fit: keep its top / left edge in view
    return Math.max(viewport - OPEN_CARD_MARGIN_PX - end, OPEN_CARD_MARGIN_PX - start);
  }
  return start < OPEN_CARD_MARGIN_PX ? OPEN_CARD_MARGIN_PX - start : 0;
};

/**
 * Pans — never zooms — just enough to bring the open card wholly inside the canvas, so a step
 * opened near an edge doesn't run past it.
 */
const useKeepOpenCardInView = (openNodeId: string | null) => {
  const { getNode, getViewport, setViewport } = useReactFlow();
  const width = useStore((state) => state.width);
  const height = useStore((state) => state.height);

  useEffect(() => {
    if (!openNodeId) return undefined;

    const timer = setTimeout(() => {
      const node = getNode(openNodeId);
      if (!node?.width || !node.height) return;

      const { x, y, zoom } = getViewport();
      const position = node.positionAbsolute ?? node.position;
      const dx = panIntoView(position.x * zoom + x, node.width * zoom, width);
      const dy = panIntoView(position.y * zoom + y, node.height * zoom, height);

      if (dx || dy) setViewport({ x: x + dx, y: y + dy, zoom }, { duration: 250 });
    }, OPEN_CARD_SETTLE_MS);

    return () => clearTimeout(timer);
  }, [getNode, getViewport, height, openNodeId, setViewport, width]);
};

/** Room for a re-layout to land before the view moves to a step it revealed. */
const RELAYOUT_SETTLE_MS = 120;
const FIT_OPTIONS = { duration: 300, padding: 0.15 } as const;

/** A key typed into a field, a select, a menu or a dialog isn't a canvas shortcut. */
const isTypingTarget = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  !!target.closest(
    'input, textarea, select, [contenteditable="true"], [role="combobox"], [role="option"], [role="dialog"], [role="menu"], [role="listbox"], .MuiModal-root',
  );

/**
 * Arrow keys move between steps only while the canvas, or nothing in particular, has focus; a
 * button or list elsewhere on the page keeps its own arrow keys.
 */
const isCanvasFocus = (target: EventTarget | null) =>
  !(target instanceof HTMLElement) ||
  target === document.body ||
  !!target.closest('.react-flow');

/**
 * Selecting a step from the toolbar, the search or the keyboard, and moving the view to it. A step
 * a fold was hiding is shown first, so the view waits for the re-layout before it centres.
 */
const useStepNavigation = ({
  nodes,
  onNodesChange,
}: {
  nodes: { id: string; selected?: boolean }[];
  onNodesChange?: (changes: NodeChange[]) => void;
}) => {
  const { getNode, getZoom, setCenter } = useReactFlow();
  const [centerTarget, setCenterTarget] = useState<string | null>(null);

  const select = useCallback(
    (nodeId: string) =>
      onNodesChange?.(
        nodes
          .filter((node) => node.selected || node.id === nodeId)
          .map((node) => ({ type: 'select', id: node.id, selected: node.id === nodeId })),
      ),
    [nodes, onNodesChange],
  );

  useEffect(() => {
    if (!centerTarget || !nodes.some(({ id }) => id === centerTarget)) return undefined;
    // selected here rather than when asked, since a step a fold hid isn't on the canvas until now
    if (!nodes.some(({ id, selected }) => id === centerTarget && selected)) {
      select(centerTarget);
    }
    const timer = setTimeout(() => {
      const node = getNode(centerTarget);
      if (!node) return;
      const position = node.positionAbsolute ?? node.position;
      setCenter(position.x + (node.width ?? 0) / 2, position.y + (node.height ?? 0) / 2, {
        zoom: Math.max(getZoom(), 0.8),
        duration: 300,
      });
      setCenterTarget(null);
    }, RELAYOUT_SETTLE_MS);
    return () => clearTimeout(timer);
  }, [centerTarget, getNode, getZoom, nodes, select, setCenter]);

  /** Selects `nodeId` and moves the view to it, once it is laid out. */
  return { goTo: setCenterTarget };
};

const DiagramContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  minHeight: 0,
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  // the focus drawer sits over the right of the canvas
  position: 'relative',
}));

const DiagramInner = ({
  nodes: initialNodes,
  edges,
  onNodeClick,
  onNodesChange,
  layoutDirection = WorkflowLayoutDirection.Down,
  onLayoutChange,
  onPaneClick,
}: {
  nodes: WorkflowGraphNode[];
  edges: WorkflowGraphEdge[];
  onNodeClick?: Parameters<typeof ReactFlow>[0]['onNodeClick'];
  onNodesChange?: Parameters<typeof ReactFlow>[0]['onNodesChange'];
  layoutDirection?: WorkflowLayoutDirection;
  onLayoutChange?: (direction: WorkflowLayoutDirection) => void;
  onPaneClick?: () => void;
}) => {
  const [isInteractive, setIsInteractive] = useState(true);
  const { openNodeId, definition, view } = useWorkflowEditor();
  useKeepOpenCardInView(openNodeId);
  const { fitView } = useReactFlow();
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const selectedId = initialNodes.find((node) => node.selected)?.id ?? null;
  const { goTo: centerOn } = useStepNavigation({ nodes: initialNodes, onNodesChange });
  const { expandAll, collapseAll, focusStep, showStep: goTo, navTarget, navDone } = view;

  // Focus, search, the arrow keys and "steps need attention" all ask for a step through the view;
  // the canvas selects and centres it once it's laid out, and marks the request done so a later
  // remount never repeats it.
  useEffect(() => {
    if (!navTarget) return;
    centerOn(navTarget.nodeId);
    navDone(navTarget);
  }, [centerOn, navDone, navTarget]);

  const fitSoon = useCallback(
    () => setTimeout(() => fitView(FIT_OPTIONS), RELAYOUT_SETTLE_MS * 2),
    [fitView],
  );
  const handleExpandAll = useCallback(() => {
    expandAll();
    fitSoon();
  }, [expandAll, fitSoon]);
  const handleCollapseAll = useCallback(() => {
    collapseAll();
    fitSoon();
  }, [collapseAll, fitSoon]);
  const handleFocusSelected = useCallback(() => {
    if (selectedId) focusStep(selectedId);
  }, [focusStep, selectedId]);

  const openSearch = useCallback(() => {
    setSearchOpen(true);
    // the field mounts on this render; focus it on the next frame
    requestAnimationFrame(() => searchInputRef.current?.focus());
  }, []);
  const { setSearch } = view;
  /** Closing the search also clears it, so nothing stays ringed or faded. */
  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    setSearch({ query: '', stepType: '' });
  }, [setSearch]);

  // Keys, as in the editor prototype: / search, E expand all, C collapse all, F fit, and the
  // arrows to move between steps — up to the step above, down to the first after, left and right
  // between the steps after the same one.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey || isTypingTarget(event.target)) {
        return;
      }
      const key = event.key.length === 1 ? event.key.toLowerCase() : event.key;

      if (key === 'Escape') {
        if (searchOpen) closeSearch();
        else if (view.focusedId) view.clearFocus();
        return;
      }

      if (key === '/') {
        event.preventDefault();
        openSearch();
        return;
      }
      // Each handled key also cancels its default: the app's own shortcuts (Mousetrap, 'c' for a
      // new conversation) listen to the keypress that would follow.
      if (key === 'e') {
        event.preventDefault();
        handleExpandAll();
        return;
      }
      if (key === 'c') {
        event.preventDefault();
        handleCollapseAll();
        return;
      }
      if (key === 'f') {
        event.preventDefault();
        fitView(FIT_OPTIONS);
        return;
      }

      if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(key)) return;
      if (!isCanvasFocus(event.target)) return;
      const root = (
        definition.nodes.find(({ type }) => type === WorkflowNodeType.Trigger) ??
        definition.nodes[0]
      )?.id;
      const from = selectedId ?? null;
      let next: string | null = null;
      if (!from) next = root ?? null;
      else if (key === 'ArrowUp') next = parentStepId(definition, from);
      else if (key === 'ArrowDown') next = childStepIds(definition, from)[0] ?? null;
      else {
        const parent = parentStepId(definition, from);
        const siblings = parent ? childStepIds(definition, parent) : [];
        next = siblings[siblings.indexOf(from) + (key === 'ArrowLeft' ? -1 : 1)] ?? null;
      }
      if (!next) return;
      event.preventDefault();
      goTo(next);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [
    definition,
    fitView,
    goTo,
    handleCollapseAll,
    handleExpandAll,
    closeSearch,
    openSearch,
    searchOpen,
    selectedId,
    view,
  ]);

  // Update nodes' draggable property based on interactive state
  const nodes = useMemo(
    () =>
      initialNodes.map((node) => ({
        ...node,
        draggable: isInteractive && node.type === STEP_NODE_TYPE && node.id !== openNodeId,
        ...(node.id === openNodeId ? { zIndex: OPEN_NODE_Z_INDEX } : {}),
      })),
    [initialNodes, isInteractive, openNodeId],
  );

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      nodeTypes={nodeTypes}
      edgeTypes={edgeTypes}
      nodesDraggable={isInteractive}
      // for MVP it's just read-only diagram
      nodesConnectable={false}
      elementsSelectable={isInteractive}
      panOnScroll
      panOnDrag
      zoomOnScroll
      zoomOnPinch
      fitView
      minZoom={0.1}
      maxZoom={4}
      proOptions={{ hideAttribution: true }}
      onNodeClick={onNodeClick}
      onNodesChange={onNodesChange}
      onPaneClick={onPaneClick}
      // A step is deleted from its card, which updates the draft; reactflow's own Backspace would
      // only take the card off the canvas. Its arrow-key nudge is off too: arrows move between steps.
      deleteKeyCode={null}
      disableKeyboardA11y
      // Focus landing near an edge — a + getting focus back as its menu closes — scrolls the
      // canvas box itself, which has no scrollbar, and pushes the tools out of view. Put it back.
      onScroll={(event) => {
        event.currentTarget.scrollTop = 0;
        event.currentTarget.scrollLeft = 0;
      }}
    >
      <Background />
      <Panel position="top-left">
        <WorkflowCanvasToolbar
          searchOpen={searchOpen}
          onSearchOpenChange={(open) => (open ? openSearch() : closeSearch())}
          searchInputRef={searchInputRef}
          selectedId={selectedId}
          onExpandAll={handleExpandAll}
          onCollapseAll={handleCollapseAll}
          onFocusSelected={handleFocusSelected}
          onFit={() => fitView(FIT_OPTIONS)}
        />
      </Panel>
      <Controls onInteractiveChange={setIsInteractive}>
        <ControlButton
          title="Vertical layout"
          aria-pressed={layoutDirection === WorkflowLayoutDirection.Down}
          onClick={() => onLayoutChange?.(WorkflowLayoutDirection.Down)}
        >
          <DynamicIcon name="flex_direction" fontSize="small" sx={{ color: 'black' }} />
        </ControlButton>
        <ControlButton
          title="Horizontal layout"
          aria-pressed={layoutDirection === WorkflowLayoutDirection.Right}
          onClick={() => onLayoutChange?.(WorkflowLayoutDirection.Right)}
        >
          <DynamicIcon name="flex_no_wrap" fontSize="small" sx={{ color: 'black' }} />
        </ControlButton>
      </Controls>
    </ReactFlow>
  );
};

DiagramInner.displayName = 'WorkflowDiagramInner';

export const WorkflowDiagram = ({
  nodes,
  edges,
  height = 560,
  onNodeClick,
  onNodesChange,
  layoutDirection,
  onLayoutChange,
  onPaneClick,
}: {
  nodes: WorkflowGraphNode[];
  edges: WorkflowGraphEdge[];
  height?: number | string;
  onNodeClick?: Parameters<typeof ReactFlow>[0]['onNodeClick'];
  onNodesChange?: Parameters<typeof ReactFlow>[0]['onNodesChange'];
  layoutDirection?: WorkflowLayoutDirection;
  onLayoutChange?: (direction: WorkflowLayoutDirection) => void;
  onPaneClick?: () => void;
}) => (
  <DiagramContainer height={height}>
    <ReactFlowProvider>
      <DiagramInner
        nodes={nodes}
        edges={edges}
        onNodeClick={onNodeClick}
        onNodesChange={onNodesChange}
        layoutDirection={layoutDirection}
        onLayoutChange={onLayoutChange}
        onPaneClick={onPaneClick}
      />
    </ReactFlowProvider>
    <WorkflowFocusDrawer />
  </DiagramContainer>
);
